import { useEffect, useRef } from "react";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { xml } from "@codemirror/lang-xml";
import { oneDark } from "@codemirror/theme-one-dark";
import { githubLight } from "@uiw/codemirror-theme-github";
import type { Extension } from "@codemirror/state";
import { varHighlight } from "../lib/cmVarHighlight";

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Defaults to "json" for backwards compat with the original BodyEditor wiring. */
  lang?: "json" | "xml";
  /** Optional env wiring for {{VAR}} chip highlighting + tooltips. */
  isResolved?: (name: string) => boolean;
  resolveValue?: (name: string) => string | undefined;
  /** Names of every env var across the workspace. Used to populate the
   *  autocomplete popover so users get the same chip experience as the
   *  URL / Params editors — type `HO`, see HOST, pick → `{{HOST}}`. */
  envVarNames?: string[];
  vars?: Record<string, string>;
}

// JSON / XML body editor backed by CodeMirror 6. Syntax highlighting,
// line numbers, bracket matching come for free. {{var}} tokens get
// decorated with chip-style spans whose color reflects whether the var
// resolves in the active environment.
export default function JsonCodeEditor({ value, onChange, lang = "json", isResolved, resolveValue, envVarNames, vars }: Props) {
  const cmRef = useRef<ReactCodeMirrorRef | null>(null);

  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  useEffect(() => {
    const obs = new MutationObserver(() => { /* parent re-renders on theme flip */ });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  const extensions: Extension[] = [
    lang === "xml" ? xml() : json(),
    ...varHighlight({ isResolved, resolveValue, envVarNames, vars }),
  ];

  return (
    <CodeMirror
      ref={cmRef}
      value={value}
      onChange={onChange}
      // Dark mode: oneDark, which blends with the app surface via the
      // transparent-bg CSS override. Light mode: GitHub Light, which
      // gives real syntax colors against a light surface (the built-in
      // CodeMirror "light" theme is basically uncolored). The light
      // theme's own light background blends naturally with the app.
      theme={isDark ? oneDark : githubLight}
      extensions={extensions}
      height="100%"
      basicSetup={{
        lineNumbers: true,
        highlightActiveLine: true,
        bracketMatching: true,
        closeBrackets: true,
        indentOnInput: true,
        autocompletion: false,
        foldGutter: false,
        searchKeymap: false,
      }}
      className="cm-host border border-border rounded h-full min-h-[160px]"
    />
  );
}
